import { getConfig } from '../config';
import { API_URL } from '../constants';
import { httpClient } from './httpClient';
import { clearSession, getValidToken, setSession } from './session';

type LoginParams = { email?: string; password?: string };

export const authProvider = {
  login: async ({ email, password }: LoginParams) => {
    if (!email || !password) {
      return Promise.reject(new Error('Email and password are required'));
    }

    const { apiKey, useEmulators, authEmulatorHost } = await getConfig();

    const signInUrl =
      useEmulators && authEmulatorHost
        ? `http://${authEmulatorHost}/identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey || 'local'}`
        : `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`;

    const resp = await fetch(signInUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, returnSecureToken: true }),
    });

    if (!resp.ok) {
      return Promise.reject(new Error('Invalid email or password'));
    }

    const { idToken, refreshToken, expiresIn } = await resp.json();
    if (!idToken) return Promise.reject(new Error('No idToken returned'));
    setSession({ idToken, refreshToken, expiresIn });

    // Authenticating with Firebase is not the same as being an admin. /me is behind
    // requireAdminAccess, so ONLY an explicit 403 means "not an admin" — reject and
    // clear. A transient failure (network, 5xx, or a 401 blip on a just-minted token)
    // must NOT lock a real admin out: let them in — the server enforces admin on every
    // request anyway, so this client check is defense-in-depth, not the boundary.
    try {
      await httpClient(`${API_URL}/me`);
    } catch (err: any) {
      if (err?.status === 403) {
        clearSession();
        return Promise.reject(new Error('This account is not authorized for the admin panel.'));
      }
    }

    return Promise.resolve();
  },

  logout: () => {
    clearSession();
    return Promise.resolve();
  },

  checkAuth: async () => {
    const token = await getValidToken();
    if (!token) return Promise.reject();

    // Re-verify admin access, not just token presence — but resiliently. getValidToken
    // already returned a FRESH token, so a 401 from /me here is almost always transient
    // (the backend also maps transient verify/Firestore errors to 401), and 5xx/network
    // are transient too — keep the session for all of those. ONLY a definitive 403
    // (valid token but not an admin — e.g. access revoked mid-session) ends the session.
    try {
      await httpClient(`${API_URL}/me`);
      return Promise.resolve();
    } catch (err: any) {
      if (err?.status === 403) {
        clearSession();
        return Promise.reject();
      }
      return Promise.resolve();
    }
  },

  checkError: (error: { status?: number }) => {
    const status = error?.status;
    if (status === 401) {
      clearSession();
      return Promise.reject({
        redirectTo: '/login',
        message: 'Your session expired. Please sign in again.',
      });
    }
    if (status === 403) {
      clearSession();
      return Promise.reject({
        redirectTo: '/login',
        message: 'This account is not authorized for the admin panel.',
      });
    }
    return Promise.resolve();
  },

  getIdentity: async () => {
    const response = await httpClient(`${API_URL}/me`);
    const { userId, email, accessLevel } = (response as any).json || {};
    return { id: userId, fullName: email, accessLevel };
  },

  getPermissions: async () => {
    try {
      const { json } = await httpClient(`${API_URL}/me`);
      return (json as any)?.accessLevel ?? 'user';
    } catch (err) {
      return Promise.reject(err as any);
    }
  },
};
